import {
  SharePointFile,
  FileListResponse,
  DriveItem,
  RawFileListResponse,
} from "../interfaces/sharePointTypes";
import { createLogger } from "../utils/logger";
import { constructRequest } from "../api/utils/apiHandler";
import { handleAuthRejection } from "../api/utils/errorHandler";

const log = createLogger("SharePointService");

// Use environment variable for API base URL - follows same pattern as other APIs
const API_BASE_URL = process.env.API_BASE_URL;

/**
 * Custom error class for SharePoint API errors - follows same pattern as other APIs
 */
export class SharePointApiError extends Error {
  errorCode: string;
  statusCode: number;
  requestId?: string;
  details?: any; // Backend error details (e.g., checked_out_to, retry_after)

  constructor(
    message: string,
    errorCode: string,
    statusCode: number,
    requestId?: string,
    details?: any
  ) {
    super(message);
    this.name = "SharePointApiError";
    this.errorCode = errorCode;
    this.statusCode = statusCode;
    this.requestId = requestId;
    this.details = details; // Store for rich error messages
  }
}

/**
 * SharePoint service for file operations using Microsoft Graph API via backend
 * Follows the same architectural pattern as chatApi.ts and contractSummaryApi.ts
 */
export class SharePointService {
  private accessToken: string;
  private folderId?: string; // Optional: backend uses configured folder if not provided

  constructor(accessToken: string, folderId?: string) {
    // Validate API_BASE_URL is configured
    if (!API_BASE_URL) {
      const error = new SharePointApiError(
        "API_BASE_URL is not configured. Please set API_BASE_URL environment variable.",
        "MISSING_CONFIG",
        500
      );
      log.error("SharePoint service initialization failed", { error: error.message });
      throw error;
    }

    // Validate access token
    if (!accessToken || accessToken.trim() === "") {
      const error = new SharePointApiError(
        "Access token is required for SharePoint operations",
        "MISSING_ACCESS_TOKEN",
        401
      );
      log.error("SharePoint service initialization failed", { error: error.message });
      throw error;
    }

    // Basic JWT format validation (header.payload.signature)
    const jwtPattern = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/;
    if (!jwtPattern.test(accessToken)) {
      const error = new SharePointApiError(
        "Invalid access token format. Expected JWT token.",
        "INVALID_ACCESS_TOKEN",
        401
      );
      log.error("SharePoint service initialization failed", { error: error.message });
      throw error;
    }

    this.accessToken = accessToken;

    // Folder ID is optional - backend uses configured folder if not provided
    // Frontend can override by passing folderId parameter to constructor
    this.folderId = folderId;

    log.info("SharePoint service initialized successfully", {
      hasFolderId: !!this.folderId,
      folderId: this.folderId ? this.folderId.substring(0, 8) + "..." : "using backend default",
    });
  }

  /**
   * List files in the configured SharePoint folder
   *
   * @param options - Query options
   * @param options.search - Optional search query to filter by filename (client-side filtering)
   * @param options.$select - OData $select parameter for field projection
   * @param options.$orderby - OData $orderby parameter (e.g., "lastModifiedDateTime desc")
   * @param options.$top - OData $top parameter for pagination (max items to return)
   * @returns FileListResponse with array of SharePointFile objects
   */
  async listFiles(options?: {
    search?: string;
    $select?: string;
    $orderby?: string;
    $top?: number;
  }): Promise<FileListResponse> {
    const params = new URLSearchParams();

    // Add folder_id as query parameter if provided (backend uses configured default if omitted)
    if (this.folderId) {
      params.append("folder_id", this.folderId);
    }

    // Add OData query parameters (supported by Microsoft Graph API)
    if (options?.$select) {
      params.append("$select", options.$select);
    }
    if (options?.$orderby) {
      params.append("$orderby", options.$orderby);
    }
    if (options?.$top) {
      params.append("$top", options.$top.toString());
    }

    const queryString = params.toString();
    const url = `${API_BASE_URL}/library/folder/files${queryString ? `?${queryString}` : ""}`;

    log.info("Listing SharePoint files", {
      endpoint: this.folderId
        ? url.replace(this.folderId, this.folderId.substring(0, 8) + "...")
        : url,
      hasSearch: !!options?.search,
      hasFolderId: !!this.folderId,
      $orderby: options?.$orderby,
      $top: options?.$top,
    });

    try {
      const request = constructRequest(url, "GET", this.accessToken);

      const response = await fetch(request);

      if (!response.ok) {
        const errorData = await this.parseErrorResponse(response);
        throw new SharePointApiError(
          errorData.message || `Failed to list files: ${response.statusText}`,
          errorData.error_code || "LIST_FILES_ERROR",
          response.status,
          errorData.request_id,
          errorData.details
        );
      }

      const data: RawFileListResponse = await response.json();
      let transformed = this.transformResponse(data);

      // Apply client-side search filtering if provided
      // Note: Backend doesn't support $filter, so we filter locally
      if (options?.search && options.search.trim()) {
        const searchLower = options.search.toLowerCase().trim();
        transformed = {
          ...transformed,
          value: transformed.value.filter((file) => file.name.toLowerCase().includes(searchLower)),
        };
        log.info("Applied client-side search filter", {
          originalCount: data.value.length,
          filteredCount: transformed.value.length,
        });
      }

      log.info("Successfully listed files", {
        fileCount: transformed.value.length,
        hasNextLink: !!transformed.nextLink,
      });

      return transformed;
    } catch (error) {
      // Re-throw if it's already our custom error type
      if (error instanceof SharePointApiError) {
        log.error("SharePoint API error in listFiles", {
          errorCode: error.errorCode,
          statusCode: error.statusCode,
          message: error.message,
        });
        throw error;
      }

      // Handle unexpected errors
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      log.error("Unexpected error listing files", { error: errorMessage });

      // Wrap in custom error type
      throw new SharePointApiError(
        `Failed to list files: ${errorMessage}`,
        "LIST_FILES_UNEXPECTED_ERROR",
        500
      );
    }
  }

  /**
   * Upload file to SharePoint folder
   * @param file - File object to upload
   * @param conflictBehavior - What to do if file exists: "fail" | "replace" | "rename" (default: "rename")
   * @returns Uploaded SharePointFile object
   */
  async uploadFile(
    file: File,
    conflictBehavior: "fail" | "replace" | "rename" = "rename"
  ): Promise<SharePointFile> {
    // Validate file
    this.validateFile(file);

    const formData = new FormData();
    formData.append("file", file);

    const params = new URLSearchParams({ conflictBehavior });

    // Add folder_id as query parameter if provided (backend uses configured default if omitted)
    if (this.folderId) {
      params.append("folder_id", this.folderId);
    }

    const url = `${API_BASE_URL}/library/folder/upload?${params.toString()}`;

    log.info("Uploading file to SharePoint", {
      endpoint: url,
      fileName: file.name,
      fileSize: file.size,
      hasFolderId: !!this.folderId,
      folderId: this.folderId ? this.folderId.substring(0, 8) + "..." : "using backend default",
    });

    try {
      const request = constructRequest(url, "POST", this.accessToken, formData);

      const response = await fetch(request);

      if (!response.ok) {
        const errorData = await this.parseErrorResponse(response);
        throw new SharePointApiError(
          errorData.message || `Failed to upload file: ${response.statusText}`,
          errorData.error_code || "UPLOAD_ERROR",
          response.status,
          errorData.request_id,
          errorData.details
        );
      }

      const data: DriveItem = await response.json();
      const transformed = this.transformDriveItem(data);

      log.info("Successfully uploaded file", {
        fileId: transformed.id,
        fileName: transformed.name,
      });

      return transformed;
    } catch (error) {
      // Re-throw if it's already our custom error type
      if (error instanceof SharePointApiError) {
        log.error("SharePoint API error in uploadFile", {
          errorCode: error.errorCode,
          statusCode: error.statusCode,
          message: error.message,
        });
        throw error;
      }

      // Handle unexpected errors
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      log.error("Unexpected error uploading file", { error: errorMessage });

      throw new SharePointApiError(
        `Failed to upload file: ${errorMessage}`,
        "UPLOAD_UNEXPECTED_ERROR",
        500
      );
    }
  }

  /**
   * Download file content from SharePoint
   * @param fileId - ID of the file to download
   * @returns Blob containing the file content
   */
  async downloadFile(fileId: string): Promise<Blob> {
    // Validate file ID
    this.validateFileId(fileId);

    const url = `${API_BASE_URL}/library/files/${fileId}/download`;

    log.info("Downloading file from SharePoint", {
      endpoint: url.replace(fileId, fileId.substring(0, 8) + "..."), // Sanitize file ID
      fileId: fileId.substring(0, 8) + "...",
    });

    try {
      const request = constructRequest(url, "GET", this.accessToken);

      const response = await fetch(request);

      if (!response.ok) {
        const errorData = await this.parseErrorResponse(response);
        throw new SharePointApiError(
          errorData.message || `Failed to download file: ${response.statusText}`,
          errorData.error_code || "DOWNLOAD_ERROR",
          response.status,
          errorData.request_id,
          errorData.details // Pass backend details through
        );
      }

      const blob = await response.blob();

      // Validate blob is not empty or corrupted
      if (blob.size === 0) {
        throw new SharePointApiError(
          "Downloaded file is empty. Please try again or contact support.",
          "EMPTY_FILE",
          500
        );
      }

      log.info("Successfully downloaded file", {
        fileId: fileId.substring(0, 8) + "...",
        size: blob.size,
        type: blob.type,
      });

      return blob;
    } catch (error) {
      // Re-throw if it's already our custom error type
      if (error instanceof SharePointApiError) {
        log.error("SharePoint API error in downloadFile", {
          errorCode: error.errorCode,
          statusCode: error.statusCode,
          message: error.message,
        });
        throw error;
      }

      // Handle unexpected errors
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      log.error("Unexpected error downloading file", { error: errorMessage });

      throw new SharePointApiError(
        `Failed to download file: ${errorMessage}`,
        "DOWNLOAD_UNEXPECTED_ERROR",
        500
      );
    }
  }

  /**
   * Download a SharePoint file and convert to File object with metadata.
   * This is the preferred method for UI components that need to work with
   * File objects (e.g., for uploading to other APIs).
   *
   * @param file - SharePointFile metadata object (must include id, name, mimeType, modifiedAt)
   * @returns File object with real binary content and proper metadata
   * @throws {SharePointApiError} If download fails or file parameter is invalid
   *
   * @example
   * const spFile = await listFiles().value[0];
   * const file = await downloadFileAsFile(spFile);
   * // file is now a proper File object ready for upload
   */
  async downloadFileAsFile(file: SharePointFile): Promise<File> {
    // Validate input
    if (!file || !file.id || !file.name) {
      throw new SharePointApiError(
        "Invalid SharePointFile parameter: must include id and name",
        "INVALID_FILE_PARAMETER",
        400
      );
    }

    try {
      log.info("Downloading SharePoint file as File object", {
        fileId: file.id.substring(0, 8) + "...",
        fileName: file.name,
        mimeType: file.mimeType,
      });

      // Download blob content
      const blob = await this.downloadFile(file.id);

      // Convert to File with proper metadata
      const fileObject = new File([blob], file.name, {
        type: file.mimeType ?? blob.type ?? "application/octet-stream",
        lastModified: file.modifiedAt?.getTime() ?? file.createdAt.getTime(),
      });

      log.info("Successfully converted SharePoint file to File object", {
        fileId: file.id.substring(0, 8) + "...",
        fileName: file.name,
        fileSize: fileObject.size,
        mimeType: fileObject.type,
      });

      return fileObject;
    } catch (error) {
      log.error("Failed to download SharePoint file as File object", {
        fileId: file.id.substring(0, 8) + "...",
        fileName: file.name,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }

  /**
   * Delete file from SharePoint
   * @param fileId - ID of the file to delete
   */
  async deleteFile(fileId: string): Promise<void> {
    // Validate file ID
    this.validateFileId(fileId);

    const url = `${API_BASE_URL}/library/files/${fileId}`;

    log.info("Deleting file from SharePoint", {
      endpoint: url.replace(fileId, fileId.substring(0, 8) + "..."), // Sanitize file ID
      fileId: fileId.substring(0, 8) + "...",
    });

    try {
      const request = constructRequest(url, "DELETE", this.accessToken);

      const response = await fetch(request);

      if (!response.ok) {
        const errorData = await this.parseErrorResponse(response);
        throw new SharePointApiError(
          errorData.message || `Failed to delete file: ${response.statusText}`,
          errorData.error_code || "DELETE_ERROR",
          response.status,
          errorData.request_id,
          errorData.details
        );
      }

      log.info("Successfully deleted file", { fileId });
    } catch (error) {
      // Re-throw if it's already our custom error type
      if (error instanceof SharePointApiError) {
        log.error("SharePoint API error in deleteFile", {
          errorCode: error.errorCode,
          statusCode: error.statusCode,
          message: error.message,
        });
        throw error;
      }

      // Handle unexpected errors
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      log.error("Unexpected error deleting file", { error: errorMessage });

      throw new SharePointApiError(
        `Failed to delete file: ${errorMessage}`,
        "DELETE_UNEXPECTED_ERROR",
        500
      );
    }
  }

  /**
   * Parse error response - handles both structured and generic FastAPI error formats
   */
  private async parseErrorResponse(response: Response): Promise<{
    message?: string;
    error_code?: string;
    request_id?: string;
    details?: any;
  }> {
    // Emit the auth-rejection telemetry and, on 401, trigger the token
    // refresh bridge - keeps SharePoint calls consistent with the API layer.
    handleAuthRejection(response);
    try {
      const errorData = await response.json();

      // Handle FastAPI HTTPException format (just "detail" field)
      // Backend returns this for generic 403/404 errors with no error_code
      if (errorData.detail && !errorData.message) {
        return {
          message: errorData.detail,
          error_code: undefined, // Generic errors have no error_code
          request_id: undefined,
          details: errorData.details,
        };
      }

      // Handle structured ErrorResponse format (with error_code)
      // Backend returns this for specific errors like SHAREPOINT_CONSENT_REQUIRED
      return {
        message: errorData.message,
        error_code: errorData.error_code,
        request_id: errorData.request_id,
        details: errorData.details,
      };
    } catch (parseError) {
      // Fallback if error response is not valid JSON
      return {
        message: `HTTP ${response.status}: ${response.statusText}`,
        error_code: "UNKNOWN_ERROR",
        request_id: "unknown",
      };
    }
  }

  /**
   * Transform backend DriveItem to frontend SharePointFile
   */
  private transformDriveItem(item: DriveItem): SharePointFile {
    return {
      id: item.id,
      name: item.name,
      size: item.size ?? null,
      mimeType: item.file?.mimeType ?? null,
      createdAt: item.createdDateTime ? new Date(item.createdDateTime) : new Date(),
      modifiedAt: item.lastModifiedDateTime ? new Date(item.lastModifiedDateTime) : null,
      webUrl: item.webUrl ?? null,
      isFolder: !!item.folder,
      folderChildCount: item.folder?.childCount ?? null,
    };
  }

  /**
   * Transform backend FileListResponse
   */
  private transformResponse(response: RawFileListResponse): FileListResponse {
    return {
      value: response.value.map((item) => this.transformDriveItem(item)),
      nextLink: response["@odata.nextLink"] ?? null,
      totalCount: response["@odata.count"] ?? null,
    };
  }

  /**
   * Validate file ID
   */
  private validateFileId(fileId: string): void {
    if (!fileId || fileId.trim() === "") {
      throw new SharePointApiError("File ID is required", "INVALID_FILE_ID", 400);
    }
  }

  /**
   * Validate file object
   */
  private validateFile(file: File): void {
    if (!file) {
      throw new SharePointApiError("File object is required", "INVALID_FILE", 400);
    }

    if (!file.name || file.name.trim() === "") {
      throw new SharePointApiError("File must have a name", "INVALID_FILE_NAME", 400);
    }

    if (file.size === 0) {
      throw new SharePointApiError("File cannot be empty", "EMPTY_FILE", 400);
    }

    // Check for reasonable file size (e.g., max 100MB)
    const maxSizeBytes = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSizeBytes) {
      throw new SharePointApiError(
        `File size exceeds maximum allowed size of ${maxSizeBytes / 1024 / 1024}MB`,
        "FILE_TOO_LARGE",
        400
      );
    }
  }
}
